import {
  type DraftRepeat,
  type DraftVisibility,
  type EventDescriptionLanguage,
  type EventDescriptionTranslations,
  type EventDraftResponse,
  type EventFeatureChoice,
  type EventMinAge,
  type EventPrice,
  type EventTicketUrlKind,
  type GeneratedEventLocation,
  type GenerateEventResponse,
  splitEventDraftPrompt,
} from '@pulsify/shared/dto'
import {
  buildMultiDayOccurrences,
  daysInRange,
  repeatOccurrences,
} from '~/entities/event/lib/event-occurrences'
import { guessTicketUrlKind } from '~/entities/event/lib/ticket-url-kind'
import type { TicketTypeInput } from '~/entities/ticket/api/tickets-api'
import type { EventEntry } from '~/features/events/lib/event-entry'
import type { ImageFile } from '~/features/shared/components/images-section'
import type { OccurrenceForm } from '~/shared/types/event'

interface AiEventHandoff {
  values: {
    title: string
    description: string
    startTime: string
    endTime: string
    occurrences: OccurrenceForm[]
    price: EventPrice
    ticketUrl: string
    ticketUrlKind: EventTicketUrlKind
    capacity: number | undefined
    tags: GenerateEventResponse['tags']
    location: GeneratedEventLocation | null
    listingId: string | null
    ticketTypes: TicketTypeInput[]
    privacy: DraftVisibility
    minAge: EventMinAge | null
    studentsOnly: boolean
    targetAgeMin: number | undefined
    targetAgeMax: number | undefined
    audienceNote: string
    locationPending: boolean
    addressHidden: boolean
  }
  images: ImageFile[]
  /** How guests get in, when the chat settled it; otherwise the form reads it from the values. */
  entry?: EventEntry | null
  /** Save the event as a draft right away instead of opening it for review. */
  autoSave?: boolean
  descriptionLanguage?: EventDescriptionLanguage
  descriptionTranslations?: EventDescriptionTranslations
  /** Features the organizer switched on or off in the AI flow. */
  features?: EventFeatureChoice
}

let pending: AiEventHandoff | null = null

/** The create form's values for an event the AI filled in, with the pictures the business added. */
export function draftToHandoff(
  draft: Omit<EventDraftResponse, 'flowId' | 'flow' | 'signupRequired'> & {
    signupRequired?: boolean
  },
  images: ImageFile[],
): AiEventHandoff {
  return {
    values: {
      title: draft.title,
      description: draft.description,
      startTime: draft.startTime ?? '',
      endTime: draft.endTime ?? '',
      occurrences: repeated(toFormOccurrences(draft.startTime, draft.endTime), draft.repeat),
      price: toFormPrice(draft.price),
      ticketUrl: draft.ticketUrl ?? '',
      ticketUrlKind: guessTicketUrlKind(draft.ticketUrl ?? '', draft.signupRequired === true),
      capacity: draft.capacity ?? undefined,
      tags: draft.tags,
      location: draft.location ?? null,
      listingId: draft.listingId ?? null,
      ticketTypes: draft.ticketTypes.map((type) => ({
        name: type.name,
        priceCents: type.priceCents,
        isStudent: type.isStudent,
        doorPriceCents: type.doorPriceCents,
        quantity: null,
      })),
      privacy: draft.visibility,
      minAge: draft.minAge,
      studentsOnly: draft.studentsOnly,
      targetAgeMin: draft.targetAgeMin ?? undefined,
      targetAgeMax: draft.targetAgeMax ?? undefined,
      audienceNote: draft.audienceNote ?? '',
      locationPending: draft.locationPending,
      addressHidden: draft.addressHidden,
    },
    images,
  }
}

const REPEAT_FREQUENCY = { day: 'days', week: 'weeks', month: 'months' } as const

function repeated(block: OccurrenceForm[], repeat: DraftRepeat | null): OccurrenceForm[] {
  if (!repeat || block.length === 0) return block
  return repeatOccurrences(block, {
    frequencyType: REPEAT_FREQUENCY[repeat.every],
    interval: repeat.interval,
    count: repeat.count,
  })
}

const EMPTY_TAGS: GenerateEventResponse['tags'] = {
  mainCategoryId: '',
  subCategoryTags: [],
  subSubCategoryTags: [],
  amenityIds: [],
}

/**
 * The create form's values when the business fills the event in itself: a link it typed
 * becomes the ticket link, the rest of the text starts the description.
 */
export function manualHandoff(prompt: string, images: ImageFile[]): AiEventHandoff {
  const { url, text } = splitEventDraftPrompt(prompt)
  return {
    values: {
      title: '',
      description: text,
      startTime: '',
      endTime: '',
      occurrences: [],
      price: { kind: 'unknown' },
      ticketUrl: url ?? '',
      ticketUrlKind: guessTicketUrlKind(url ?? ''),
      capacity: undefined,
      tags: EMPTY_TAGS,
      location: null,
      listingId: null,
      ticketTypes: [],
      privacy: 'public',
      minAge: null,
      studentsOnly: false,
      targetAgeMin: undefined,
      targetAgeMax: undefined,
      audienceNote: '',
      locationPending: false,
      addressHidden: false,
    },
    images,
  }
}

const SOCIAL_NETWORKS: Record<string, string> = {
  'instagram.com': 'Instagram',
  'facebook.com': 'Facebook',
  'fb.com': 'Facebook',
  'fb.me': 'Facebook',
  'threads.net': 'Threads',
  'threads.com': 'Threads',
  'tiktok.com': 'TikTok',
}

/** The social network a typed link points to, which hides posts behind its login. */
export function socialNetworkOf(prompt: string): string | null {
  const { url } = splitEventDraftPrompt(prompt)
  if (!url) return null
  try {
    const host = new URL(url).hostname.toLowerCase()
    const match = Object.keys(SOCIAL_NETWORKS).find(
      (social) => host === social || host.endsWith(`.${social}`),
    )
    return match ? SOCIAL_NETWORKS[match] : null
  } catch {
    return null
  }
}

const INPUT_TO_FIX = new Set([
  'ValidationFailed',
  'EventDraftEmpty',
  'UnsupportedImageType',
  'ImageTooLarge',
  'InsufficientCredits',
])

type DraftFailureAction = 'fix-input' | 'add-screenshot' | 'fill-in-manually'

/** What the start page does when the event could not be filled in. */
export function draftFailureAction(
  errorCode: string,
  input: { prompt: string; pictureCount: number },
): DraftFailureAction {
  if (INPUT_TO_FIX.has(errorCode)) return 'fix-input'
  const socialPost = socialNetworkOf(input.prompt) !== null
  if (errorCode === 'EventPageUnreadable' && input.pictureCount === 0 && socialPost) {
    return 'add-screenshot'
  }
  return 'fill-in-manually'
}

export type HandoffLocationNotice = 'missing' | 'unconfirmed' | null

/** Whether the location card has to ask for the place: none found, or found but not pinned. */
export function handoffLocationNotice(values: AiEventHandoff['values']): HandoffLocationNotice {
  if (!values.location) return 'missing'
  if (!values.listingId && !values.location.coordinates) return 'unconfirmed'
  return null
}

export function setAiEventHandoff(handoff: AiEventHandoff): void {
  pending = handoff
}

export function hasPendingAiEventHandoff(): boolean {
  return pending !== null
}

export function consumeAiEventHandoff(): AiEventHandoff | null {
  const value = pending
  pending = null
  return value
}

/**
 * Turns the model's `YYYY-MM-DDTHH:mm` range into the per-day rows the date
 * section actually edits. Without this the generated schedule reaches the form
 * only as the top-level start/end, which no field on the page renders - the
 * event would then be saved on a date the user never got to see.
 */
export function toFormOccurrences(
  startTime: string | null,
  endTime: string | null,
): OccurrenceForm[] {
  const [startDate, startClock] = (startTime ?? '').split('T')
  if (!startDate || !startClock) return []

  const [endDate, endClock] = (endTime ?? '').split('T')
  const start = startClock.slice(0, 5)
  const end = (endClock ?? '').slice(0, 5)

  // An end that lands on the following day without getting later in the day is
  // one night out, not two dates: occurrenceFormToRequest already rolls such a
  // row past midnight, so emitting a second row would duplicate the event.
  const overnight = daysInRange(startDate, endDate).length === 2 && end !== '' && end <= start

  // A missing end stays empty rather than guessing: the occurrences editor
  // flags it, which is honest about what the model did not supply.
  return buildMultiDayOccurrences(
    startDate,
    overnight ? startDate : endDate || startDate,
    start,
    end,
  )
}

/** Narrow the server's price-info shape into the form's discriminated union. */
function toFormPrice(price: GenerateEventResponse['price']): EventPrice {
  if (price.kind === 'fixed' && price.priceFixed != null) {
    return { kind: 'fixed', priceFixed: price.priceFixed }
  }
  if (price.kind === 'range' && price.priceRangeMin != null && price.priceRangeMax != null) {
    return {
      kind: 'range',
      priceRangeMin: price.priceRangeMin,
      priceRangeMax: price.priceRangeMax,
    }
  }
  return { kind: price.kind === 'free' ? 'free' : 'unknown' }
}
